import type { ExistingSnapshotPosition } from "@repo/shared/types";
import { AlertTriangle } from "lucide-react";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./dropped-instruments-section.i18n";

type DroppedInstrumentsSectionProps = {
  /** Agreement-baseline lines with no matching active product (D5-r). */
  holdings: ExistingSnapshotPosition[];
};

/**
 * Agreement follow-up only: the frozen lines that match no active product in
 * the flow's collection. They are not part of the advised draft below — the
 * replace-all completion drops them at the bank — so they render apart from
 * the valid instruments, above the table (D5-r, user decision 2026-09-07).
 */
export function DroppedInstrumentsSection({ holdings }: DroppedInstrumentsSectionProps) {
  const t = useT(T);
  const { formatNumber } = useRegionalFormat();
  if (holdings.length === 0) return null;
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-4 py-3 space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-500">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
        {t.droppedSectionTitle}
      </div>
      <div className="divide-y">
        {holdings.map((h) => (
          <div key={h.id} className="flex items-baseline gap-3 py-1.5 text-sm">
            <span className="min-w-0 truncate font-medium">{h.name || "—"}</span>
            <span className="shrink-0 text-muted-foreground text-xs">
              {h.isin ?? t.droppedNoIsin}
            </span>
            <span className="ml-auto shrink-0 text-right tabular-nums">
              {formatNumber(h.amount)}
            </span>
          </div>
        ))}
      </div>
      <p className="text-muted-foreground text-xs">{t.droppedSectionInfo}</p>
    </div>
  );
}
